import React, { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TimeReportItem } from '../../types';

interface TimeReportProps {
  periods: TimeReportItem[];
  periodType: string;
  grandTotal: number;
  totalExpenses: number;
  projectName?: string;
}

const COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];

const TimeReport: React.FC<TimeReportProps> = ({
  periods,
  periodType,
  grandTotal,
  totalExpenses,
  projectName,
}) => {
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  const togglePeriod = (period: string) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(period)) {
      newExpanded.delete(period);
    } else {
      newExpanded.add(period);
    }
    setExpandedPeriods(newExpanded);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPeriodTypeLabel = () => {
    switch (periodType) {
      case 'week':
        return 'Semanal';
      case 'biweekly':
        return 'Quincenal';
      case 'month':
        return 'Mensual';
      case 'custom':
        return 'Personalizado';
      default:
        return periodType;
    }
  };

  // Prepare data for trend chart
  const trendData = periods.map((period) => ({
    period: period.periodLabel,
    amount: period.totalAmount,
    expenses: period.expenseCount,
  }));

  // Get all unique categories across all periods
  const allCategories = new Set<string>();
  periods.forEach((period) => {
    period.categories.forEach((cat) => {
      allCategories.add(cat.categoryName);
    });
  });

  // Prepare data for stacked bar chart with categories
  const stackedData = periods.map((period) => {
    const data: any = {
      period: period.periodLabel,
    };
    period.categories.forEach((cat) => {
      data[cat.categoryName] = cat.totalAmount;
    });
    return data;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Reporte por Período de Tiempo
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Tipo de Período:</span>
            <span className="ml-2 font-semibold">{getPeriodTypeLabel()}</span>
          </div>
          {projectName && (
            <div>
              <span className="text-gray-600">Proyecto:</span>
              <span className="ml-2 font-semibold">{projectName}</span>
            </div>
          )}
          <div>
            <span className="text-gray-600">Total Gastos:</span>
            <span className="ml-2 font-semibold">{totalExpenses}</span>
          </div>
          <div>
            <span className="text-gray-600">Monto Total:</span>
            <span className="ml-2 font-semibold text-blue-600">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Chart Type Toggle */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Tipo de Gráfico:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setChartType('line')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                chartType === 'line'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Línea
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                chartType === 'bar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Barras
            </button>
          </div>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Tendencia de Gastos</h3>
        <div className="h-64 lg:h-96">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart
                data={trendData}
                margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  className="text-xs lg:text-sm"
                />
                <YAxis className="text-xs lg:text-sm" />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'amount') return formatCurrency(value);
                    return value;
                  }}
                  labelStyle={{ color: '#000' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Monto"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Cantidad de Gastos"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            ) : (
              <BarChart
                data={trendData}
                margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  className="text-xs lg:text-sm"
                />
                <YAxis className="text-xs lg:text-sm" />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelStyle={{ color: '#000' }}
                />
                <Bar dataKey="amount" fill="#3b82f6" name="Monto" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stacked Bar Chart by Category */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">
          Distribución por Categoría en el Tiempo
        </h3>
        <div className="h-64 lg:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stackedData}
              margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                angle={-45}
                textAnchor="end"
                height={80}
                className="text-xs lg:text-sm"
              />
              <YAxis className="text-xs lg:text-sm" />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                labelStyle={{ color: '#000' }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {Array.from(allCategories).map((categoryName, index) => (
                <Bar
                  key={categoryName}
                  dataKey={categoryName}
                  stackId="a"
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Table - Desktop */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fechas
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gastos
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periods.map((period) => (
                <React.Fragment key={period.period}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {period.periodLabel}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {period.startDate} - {period.endDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {period.expenseCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(period.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => togglePeriod(period.period)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {expandedPeriods.has(period.period)
                          ? 'Ocultar'
                          : 'Ver Categorías'}
                      </button>
                    </td>
                  </tr>
                  {expandedPeriods.has(period.period) &&
                    period.categories.map((category) => (
                      <tr key={category.categoryId} className="bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap pl-12" colSpan={2}>
                          <div className="text-sm text-gray-700">
                            └─ {category.categoryName}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                          {category.expenseCount}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                          {formatCurrency(category.totalAmount)}
                        </td>
                        <td className="px-6 py-3"></td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-gray-100">
              <tr>
                <td className="px-6 py-4 font-bold text-gray-900" colSpan={2}>
                  Total
                </td>
                <td className="px-6 py-4 text-right font-bold text-gray-900">
                  {totalExpenses}
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  {formatCurrency(grandTotal)}
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden p-4 space-y-4">
          {periods.map((period) => (
            <div key={period.period} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-semibold text-gray-900">
                    {period.periodLabel}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {period.startDate} - {period.endDate}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">
                    {formatCurrency(period.totalAmount)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {period.expenseCount} gastos
                  </div>
                </div>
              </div>
              <button
                onClick={() => togglePeriod(period.period)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {expandedPeriods.has(period.period)
                  ? 'Ocultar Categorías'
                  : 'Ver Categorías'}
              </button>
              {expandedPeriods.has(period.period) && (
                <div className="mt-3 pl-4 space-y-2 border-l-2 border-gray-200">
                  {period.categories.map((category) => (
                    <div
                      key={category.categoryId}
                      className="flex justify-between items-center text-sm"
                    >
                      <span className="text-gray-700">{category.categoryName}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(category.totalAmount)}
                        </div>
                        <div className="text-xs text-gray-600">
                          {category.expenseCount} gastos
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="border-t-2 border-gray-300 pt-4 flex justify-between items-center font-bold">
            <span className="text-gray-900">Total</span>
            <div className="text-right">
              <div className="text-lg text-blue-600">
                {formatCurrency(grandTotal)}
              </div>
              <div className="text-sm text-gray-600">{totalExpenses} gastos</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeReport;
